'use client'

import { useState, useMemo, useCallback } from 'react'
import { FilterState, ThreatDataResult, FilterOption } from './ThreatLibrary.interface'

// Default filters
const DEFAULT_FILTERS: FilterState = {
  threatName: '',
  signatureId: '',
  category: [''],
  severity: [''],
  direction: [''],
  product: [''],
  engines: [''],
  azListing: [],
  specialCharacters: false,
}

// Dropdown options - taken from threatlabz-client
export const DROPDOWN_OPTIONS: Record<string, FilterOption[]> = {
  category: [
    { id: 0, label: 'ALL', value: '' },
    { id: 17, label: 'Malware', value: 'malware' },
    { id: 1, label: 'Advanced Threats', value: 'ADVANCED_HEADER', isHeader: true },
    {
      id: 2,
      label: 'Ad Tracker',
      value: 'adware',
      category: 'Advanced Threats',
      isSubOption: true,
    },
    {
      id: 3,
      label: 'Anonymizer',
      value: 'anonymizer',
      category: 'Advanced Threats',
      isSubOption: true,
    },
    {
      id: 4,
      label: 'Botnet Callback',
      value: 'botnet-callback',
      category: 'Advanced Threats',
      isSubOption: true,
    },
    {
      id: 40,
      label: 'Browser Exploit',
      value: 'browser-exploit',
      category: 'Advanced Threats',
      isSubOption: true,
    },
    {
      id: 5,
      label: 'Cookie Stealer',
      value: 'cookie-stealer',
      category: 'Advanced Threats',
      isSubOption: true,
    },
    {
      id: 6,
      label: 'Cryptominer',
      value: 'cryptominer',
      category: 'Advanced Threats',
      isSubOption: true,
    },
    {
      id: 7,
      label: 'Denial of Service',
      value: 'denial-of-service',
      category: 'Advanced Threats',
      isSubOption: true,
    },
    { id: 8, label: 'Exploit', value: 'exploit', category: 'Advanced Threats', isSubOption: true },
    { id: 9, label: 'IoT', value: 'iot', category: 'Advanced Threats', isSubOption: true },
    { id: 10, label: 'IRC', value: 'irc', category: 'Advanced Threats', isSubOption: true },
    {
      id: 11,
      label: 'Malicious Url',
      value: 'malicious-url',
      category: 'Advanced Threats',
      isSubOption: true,
    },
    {
      id: 12,
      label: 'Phishing',
      value: 'phishing',
      category: 'Advanced Threats',
      isSubOption: true,
    },
    { id: 13, label: 'Spam', value: 'spam', category: 'Advanced Threats', isSubOption: true },
    { id: 14, label: 'Spyware', value: 'spyware', category: 'Advanced Threats', isSubOption: true },
    {
      id: 15,
      label: 'Suspicious',
      value: 'suspicious',
      category: 'Advanced Threats',
      isSubOption: true,
    },
    {
      id: 16,
      label: 'Cross-site Scripting',
      value: 'xss',
      category: 'Advanced Threats',
      isSubOption: true,
    },
  ],
  severity: [
    { id: 1, label: 'ALL', value: '' },
    { id: 2, label: 'Critical (90-100)', value: 'critical' },
    { id: 3, label: 'High (60-89)', value: 'high' },
    { id: 4, label: 'Medium (30-59)', value: 'medium' },
    { id: 5, label: 'Low (0-29)', value: 'low' },
  ],
  direction: [
    { id: 1, label: 'ALL', value: '' },
    { id: 2, label: 'Inbound', value: 'inbound' },
    { id: 3, label: 'Outbound', value: 'outbound' },
  ],
  engines: [
    { id: 1, label: 'ALL', value: '' },
    { id: 6, label: 'Malware', value: 'malware' },
    { id: 2, label: 'File Reputation', value: 'file-reputation' },
    { id: 3, label: 'Inline Yara', value: 'inline-yara' },
    { id: 4, label: 'IPS (non-web)', value: 'ips-non-web' },
    { id: 5, label: 'IPS (web)', value: 'ips-web' },
    { id: 7, label: 'Reputation', value: 'reputation' },
    { id: 8, label: 'Sandbox', value: 'sandbox' },
    { id: 9, label: 'WAF', value: 'waf' },
  ],
  product: [
    { id: 1, label: 'Both', value: '' },
    { id: 2, label: 'ZIA', value: 'zia' },
    { id: 3, label: 'ZPA', value: 'zpa' },
  ],
}

/**
 * Utility to convert current filters state into API query parameters.
 */
export const getApiParams = (
  filters: FilterState,
  pageNo: number = 1,
): Record<string, string | number> => {
  const params: Record<string, string | number> = {}

  // Helper to get comma-separated string, excluding ALL/Both/empty defaults
  const formatMultiSelect = (values: string[]): string => {
    const active = values.filter((v) => v !== 'ALL' && v !== 'Both' && v !== '')
    return active.join(',')
  }

  // 1. Text Filters (threatname, sigid)
  if (filters.threatName) {
    params['threatname'] = filters.threatName
  }
  if (filters.signatureId) {
    params['sigid'] = filters.signatureId
  }

  // 2. Multi-Select Filters
  const categoryStr = formatMultiSelect(filters.category)
  if (categoryStr) {
    params['category'] = categoryStr
  }

  const severityStr = formatMultiSelect(filters.severity)
  if (severityStr) {
    params['severity'] = severityStr
  }

  const directionStr = formatMultiSelect(filters.direction)
  if (directionStr) {
    params['direction'] = directionStr
  }

  const engineStr = formatMultiSelect(filters.engines)
  if (engineStr) {
    params['engine'] = engineStr
  }

  const productStr = formatMultiSelect(filters.product)
  if (productStr) {
    params['product'] = productStr
  }

  // 3. A-Z Listing
  const azListingStr = filters.azListing.filter((v) => v)
  if (azListingStr.length > 0) {
    params['letter'] = azListingStr.join(',')
  }

  // 4. Pagination
  if (pageNo > 1) {
    params['page'] = pageNo
  }

  return params
}

export const useThreatFilters = (
  allThreats: ThreatDataResult[],
  initialUrlFilters: Partial<FilterState> = {},
) => {
  // 1. Initial State: Merge defaults with URL parameters
  const mergedInitialFilters: FilterState = useMemo(
    () =>
      ({
        ...DEFAULT_FILTERS,
        ...initialUrlFilters,
      }) as FilterState,
    [initialUrlFilters],
  )

  const [filters, setFilters] = useState<FilterState>(mergedInitialFilters)
  const [isPanelOpen, setIsPanelOpen] = useState(false)

  // 2. State Actions

  const resetFilters = useCallback(() => {
    setFilters({ ...DEFAULT_FILTERS })
  }, [])

  const updateFilter = useCallback(
    <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
      // --- START SINGLE-SELECT ENFORCEMENT ---
      const isSingleSelect = key === 'direction' || key === 'product'

      if (isSingleSelect && Array.isArray(value)) {
        const newValue = value.length > 0 ? value[value.length - 1] : '' // Get the last clicked value (string)
        const newArray = [newValue]

        // The default for product is 'Both', for direction is ''. We rely on the initial state logic.
        if (newValue === 'ALL' || newValue === 'Both' || newValue === '') {
          // If the user clicks a default, array is [''] or ['Both']
          setFilters((prev) => ({ ...prev, [key]: newArray as FilterState[K] }))
          return
        }

        // If a specific value is clicked, set the state to just that value.
        setFilters((prev) => ({ ...prev, [key]: newArray as FilterState[K] }))
        return
      }
      // --- END SINGLE-SELECT ENFORCEMENT ---

      // CATEGORY MUTUAL EXCLUSION LOGIC (Remains the same)
      if (key === 'category' && Array.isArray(value)) {
        const categories = value as string[]
        const MALWARE_VALUE = 'malware'
        const ALL_VALUE = ''

        const advancedThreatValues = DROPDOWN_OPTIONS.category
          .filter((opt) => opt.value !== ALL_VALUE && opt.value !== MALWARE_VALUE && !opt.isHeader)
          .map((opt) => opt.value)

        let newSelection = [...categories]

        const newlySelectedValues = categories.filter((v) => !filters.category.includes(v))
        const newlySelectedValue = newlySelectedValues[0]

        // Clean the array of 'ALL' if any specific filter is selected
        if (newSelection.includes(ALL_VALUE) && newSelection.length > 1) {
          newSelection = newSelection.filter((v) => v !== ALL_VALUE)
        }

        // utual Exclusion Rules

        if (newlySelectedValue === ALL_VALUE) {
          newSelection = [ALL_VALUE]
        } else if (newlySelectedValue === MALWARE_VALUE) {
          newSelection = [MALWARE_VALUE]
        } else if (advancedThreatValues.includes(newlySelectedValue)) {
          newSelection = newSelection.filter((v) => v !== MALWARE_VALUE && v !== ALL_VALUE)
        }

        // Handle deselection or ensure the final state is valid.
        const hasMalwareNow = newSelection.includes(MALWARE_VALUE)
        const hasAdvancedThreatsNow = newSelection.some((v) => advancedThreatValues.includes(v))

        if (hasMalwareNow && hasAdvancedThreatsNow) {
          newSelection = newSelection.filter((v) => v !== MALWARE_VALUE)
        }

        // Prevent empty array, default to ALL
        if (newSelection.length === 0) {
          newSelection = [ALL_VALUE]
        }

        // Apply final value
        setFilters((prev) => ({ ...prev, [key]: newSelection as FilterState[K] }))
        return
      }

      // Default update for all other fields
      setFilters((prev) => ({ ...prev, [key]: value }))
    },
    [filters],
  )

  const clearAZFilter = useCallback(() => {
    setFilters((prev) => ({ ...prev, azListing: [] }))
  }, [])

  // Filtering Logic (for local results preview or initial mock load)
  const filteredResults = useMemo(() => {
    const results = allThreats

    return results
  }, [allThreats])

  // Return Values
  return {
    filters,
    updateFilter,
    resetFilters,
    filteredResults,
    resultsCount: filteredResults.length,
    isPanelOpen,
    setIsPanelOpen,
    clearAZFilter,
    getApiParams,
  }
}
